<!-- RefundResultModal.vue
     Modal informativo exibido ao usuário após uma tentativa de estorno
     (botão "Solicitar reembolso" em AccountSummary).

     Cobre três caminhos:
       1. Falha de comunicação (HTTP error / rede)        → variant="warn"
       2. Backend OK mas MP recusou o estorno             → variant="warn"
       3. Estorno realizado com sucesso no MP             → variant="success"

     Segue o mesmo padrão visual de SubscriptionExpiredModal.vue:
       - painel branco centrado, alinhado pelo topo
       - ícone redondo no topo (cor varia com o variant)
       - título / corpo / ações em coluna
       - animação fade + scale via <transition>
       - uma única ação ("Fechar"), centralizada
-->

<script setup>
import { onMounted, onBeforeUnmount, computed } from "vue";

const props = defineProps({
  show: { type: Boolean, default: false },
  // Mensagem textual completa exibida no corpo. Vem do backend (result.message)
  // ou do erro capturado (err.message). Não é montada aqui.
  message: { type: String, required: true },
  // "warn" para os caminhos 1 e 2 (falha/estorno não confirmado),
  // "success" para o caminho 3 (estorno confirmado).
  variant: {
    type: String,
    default: "warn",
    validator: (v) => ["warn", "success"].includes(v),
  },
  // Título exibido no topo. Se não informado, usa fallback baseado no variant.
  title: { type: String, default: null },
});

const emit = defineEmits(["close"]);

const resolvedTitle = computed(() => {
  if (props.title) return props.title;
  return props.variant === "success"
    ? "Estorno realizado"
    : "Aviso sobre o Estorno";
});

function close() {
  emit("close");
}

function handleEscKey(event) {
  if (!props.show) return;
  if (event.key === "Escape") close();
}

onMounted(() => document.addEventListener("keydown", handleEscKey));
onBeforeUnmount(() => document.removeEventListener("keydown", handleEscKey));
</script>

<template>
  <Teleport to="body">
    <transition name="modal-fade" appear>
      <div
        v-if="show"
        class="modal-backdrop"
        role="dialog"
        aria-modal="true"
        aria-labelledby="refund-result-title"
        @click.self="close"
      >
        <div class="modal-panel modal-panel--compact">
          <div
            class="modal-panel__icon"
            :class="{
 'modal-panel__icon--warn': variant === 'warn',
              'modal-panel__icon--success': variant === 'success',
            }"
          >
            <!-- Triângulo de alerta para warn -->
            <svg
              v-if="variant === 'warn'"
              class="modal-panel__icon-svg"
              fill="currentColor"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path
                fill-rule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clip-rule="evenodd"
              />
            </svg>

            <!-- Check para success -->
            <svg
              v-else
              class="modal-panel__icon-svg"
              fill="currentColor"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                clip-rule="evenodd"
              />
            </svg>
          </div>

          <h4 id="refund-result-title" class="modal-panel__title">
            {{ resolvedTitle }}
          </h4>

          <p class="modal-panel__body">{{ message }}</p>

          <div class="modal-panel__actions">
            <button type="button" class="btn btn-secondary" @click="close">
              Fechar
            </button>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<style scoped>
/* Bolha redonda do ícone — duas variantes de cor.
   warn: âmbar (alerta informativo, mesmo do SubscriptionExpiredModal).
   success: verde (estorno confirmado). */
.modal-panel__icon {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.25rem;
}

.modal-panel__icon--warn {
  background: var(--warning-bg);
  border: 1px solid var(--warning-bg);
  color: #b45309;
}

.modal-panel__icon--success {
  background: var(--success-bg);
  border: 1px solid #86efac;
  color: #15803d;
}

.modal-panel__icon-svg {
  width: 1.5rem;
  height: 1.5rem;
}

.modal-panel__title {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--text-dark);
  margin: 0;
}

.modal-panel__body {
  font-size: 0.9rem;
  color: var(--text-light);
  line-height: 1.5;
  margin: 0;
  /* <<< MUDANÇA: parágrafo justificado. */
  text-align: justify;
}

.modal-panel__actions {
  display: flex;
  gap: 0.65rem;
  margin-top: 0.5rem;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.25s ease;
}

.modal-fade-enter-active .modal-panel,
.modal-fade-leave-active .modal-panel {
  transition: transform 0.25s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

.modal-fade-enter-from .modal-panel,
.modal-fade-leave-to .modal-panel {
  transform: scale(0.96) translateY(-8px);
}
</style>